import { useState } from 'react'
import { Link } from 'react-router'
import {
  Button,
  CollapsibleCard,
  IconButton,
  Input,
  Person,
  SectionLabel,
} from '../../../design/components'
import type { CampaignCharacter, CampaignLocation, LocationsSection } from '../types'
import type { CampaignEditing } from '../useCampaignEditing'
import styles from './sections.module.css'

/**
 * Places — design spec §5.4's landmarks. Section `locations`.
 *
 * Each place carries the people found there (with the GM's note about why), and the
 * encounters planned at it. The main depiction is shown here; the rest of the gallery,
 * uploads and the two generators are a screen of their own — a row in a list is the wrong
 * place for a grid of thumbnails.
 */
export function LocationsCard({
  campaignId,
  locations,
  roster,
  editing,
}: {
  campaignId: number
  locations: LocationsSection | undefined
  /** The campaign roster — a location's cast must already be in play. */
  roster: readonly CampaignCharacter[]
  editing: CampaignEditing
}) {
  const [adding, setAdding] = useState(false)

  if (locations === undefined) return null

  const all = locations.locations ?? []
  const canEdit = editing.access.isGameMaster

  return (
    <CollapsibleCard
      variant="magic"
      label="Places"
      summary={all.length === 1 ? '1 place' : `${all.length} places`}
      defaultOpen={all.length > 0}
      action={
        canEdit && !adding ? (
          <IconButton label="Add a place" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {all.length === 0 && !adding && <p className={styles.empty}>No places in this city yet.</p>}

      {all.map((location) => (
        <LocationRow
          key={location.id}
          campaignId={campaignId}
          location={location}
          roster={roster}
          editing={editing}
        />
      ))}

      {adding && <LocationForm editing={editing} onClose={() => setAdding(false)} />}
    </CollapsibleCard>
  )
}

function LocationRow({
  campaignId,
  location,
  roster,
  editing,
}: {
  campaignId: number
  location: CampaignLocation
  roster: readonly CampaignCharacter[]
  editing: CampaignEditing
}) {
  const [open, setOpen] = useState<'none' | 'edit' | 'character' | 'encounter'>('none')
  const canEdit = editing.access.isGameMaster

  if (open === 'edit') {
    return <LocationForm location={location} editing={editing} onClose={() => setOpen('none')} />
  }

  const image = location.imageUrl

  return (
    <div className={styles.row}>
      <div className={styles.rowHead}>
        <span className={styles.rowName}>{location.name}</span>
        {canEdit && (
          <IconButton label={`Edit ${location.name}`} icon="edit" onClick={() => setOpen('edit')} />
        )}
      </div>

      {image !== null && image !== '' && (
        <img className={styles.placeImage} src={image} alt="" loading="lazy" />
      )}

      {location.description !== '' && <p className={styles.rowText}>{location.description}</p>}

      <div className={styles.subBlock}>
        <SectionLabel tone="muted" micro>
          Who is here
        </SectionLabel>

        {location.characters.length === 0 ? (
          <p className={styles.empty}>Nobody placed here.</p>
        ) : (
          <div className={styles.subList}>
            {location.characters.map((entry) => (
              <div key={entry.id} className={styles.subRow}>
                <Person
                  name={entry.characterName ?? 'Unknown character'}
                  portraitUrl={entry.portraitUrl}
                  meta={entry.notes === '' ? null : entry.notes}
                  characterId={entry.characterId}
                />
                {canEdit && (
                  <Button
                    variant="danger"
                    disabled={editing.removeLocationCharacter.isPending}
                    onClick={() =>
                      editing.removeLocationCharacter.mutate({ locationCharacterId: entry.id })
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Planned encounters are the GM's own prep. Stage 7 links them to the real
          encounter resource; here they are the notes that live on the place. */}
      {(canEdit || location.plannedEncounters.length > 0) && (
        <div className={styles.subBlock}>
          <SectionLabel tone="muted" micro>
            Planned encounters
          </SectionLabel>

          {location.plannedEncounters.length === 0 ? (
            <p className={styles.empty}>Nothing planned here.</p>
          ) : (
            <div className={styles.subList}>
              {location.plannedEncounters.map((encounter) => (
                <div key={encounter.id} className={styles.subRow}>
                  <span className={styles.personText}>
                    <span className={styles.rowName}>{encounter.name}</span>
                    {encounter.description !== '' && (
                      <span className={styles.rowMeta}>{encounter.description}</span>
                    )}
                  </span>
                  {canEdit && (
                    <Button
                      variant="danger"
                      disabled={editing.removeLocationEncounter.isPending}
                      onClick={() =>
                        editing.removeLocationEncounter.mutate({ encounterId: encounter.id })
                      }
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canEdit && open === 'character' && (
        <LocationCharacterForm
          location={location}
          roster={roster}
          editing={editing}
          onClose={() => setOpen('none')}
        />
      )}

      {canEdit && open === 'encounter' && (
        <EncounterForm location={location} editing={editing} onClose={() => setOpen('none')} />
      )}

      {open === 'none' && (
        <div className={styles.rowActions}>
          {/* Everyone gets the gallery link: images are visible to any member, and the
              gallery's own `canManage` decides who may change them. */}
          <Link
            to={`/campaigns/${campaignId}/locations/${location.id}/images`}
            className={styles.cardLink}
          >
            <Button variant="secondary" fullWidth>
              Images
            </Button>
          </Link>
        </div>
      )}

      {canEdit && open === 'none' && (
        <div className={styles.rowActions}>
          <Button
            variant="secondary"
            disabled={roster.length === 0}
            onClick={() => setOpen('character')}
          >
            Place a character
          </Button>
          <Button variant="secondary" onClick={() => setOpen('encounter')}>
            Plan an encounter
          </Button>
          <Button
            variant="danger"
            disabled={editing.removeLocation.isPending}
            onClick={() => editing.removeLocation.mutate({ locationId: location.id })}
          >
            Delete
          </Button>
        </div>
      )}
    </div>
  )
}

function LocationForm({
  location,
  editing,
  onClose,
}: {
  location?: CampaignLocation | undefined
  editing: CampaignEditing
  onClose: () => void
}) {
  const [name, setName] = useState(location?.name ?? '')
  const [description, setDescription] = useState(location?.description ?? '')

  const creating = location === undefined
  const pending = editing.addLocation.isPending || editing.updateLocation.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        const body = { name, description: description === '' ? null : description }

        if (creating) {
          editing.addLocation.mutate(body, { onSuccess: onClose })
        } else {
          editing.updateLocation.mutate({ locationId: location.id, body }, { onSuccess: onClose })
        }
      }}
    >
      <SectionLabel tone="ember" micro>
        {creating ? 'New place' : `Edit ${location.name}`}
      </SectionLabel>

      <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Description
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || name.trim() === ''}>
          {pending ? 'Saving…' : creating ? 'Add' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

function LocationCharacterForm({
  location,
  roster,
  editing,
  onClose,
}: {
  location: CampaignLocation
  roster: readonly CampaignCharacter[]
  editing: CampaignEditing
  onClose: () => void
}) {
  const alreadyHere = new Set(location.characters.map((entry) => entry.characterId))
  const candidates = roster.filter((character) => !alreadyHere.has(character.characterId))

  const [characterId, setCharacterId] = useState(String(candidates[0]?.characterId ?? ''))
  const [notes, setNotes] = useState('')

  const pending = editing.addLocationCharacter.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        editing.addLocationCharacter.mutate(
          {
            locationId: location.id,
            body: { characterId: Number(characterId), notes: notes === '' ? null : notes },
          },
          { onSuccess: onClose },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        Place someone at {location.name}
      </SectionLabel>

      {candidates.length === 0 ? (
        <p className={styles.note}>Everyone on the roster is already here.</p>
      ) : (
        <>
          <label className={styles.field}>
            <SectionLabel tone="muted" micro>
              Character
            </SectionLabel>
            <select
              className={styles.select}
              value={characterId}
              onChange={(e) => setCharacterId(e.target.value)}
            >
              {candidates.map((character) => (
                <option key={character.characterId} value={character.characterId}>
                  {character.name}
                </option>
              ))}
            </select>
          </label>

          <Input
            label="Why they are here"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </>
      )}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || characterId === '' || candidates.length === 0}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}

function EncounterForm({
  location,
  editing,
  onClose,
}: {
  location: CampaignLocation
  editing: CampaignEditing
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const pending = editing.addLocationEncounter.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        editing.addLocationEncounter.mutate(
          {
            locationId: location.id,
            body: { name, description: description === '' ? null : description },
          },
          { onSuccess: onClose },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        Plan an encounter at {location.name}
      </SectionLabel>

      <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Description
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || name.trim() === ''}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}
